import classNames from 'classnames';
import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import type { IObjectTreeIcon } from '../../../../common/src/domain/object/IObjectTreeIcon';
import { SurveyScopeExclusion } from '../../../../common/src/domain/surveyScopeExclusion/surveyScopeExclusionTableSchema';
import { FeatureFlags } from '../../../../common/src/FeatureFlags';
import { customInspectionTypeFrontEndSettings } from '../../customInspectionTypes/customInspectionTypeFrontEndSettings';
import { ITranslateProps, translate } from '../../mixins/translate';
import { fetchJson } from '../../store/fetch';
import { InspectionStandardTypes } from '../../utils/enums/InspectionStandardTypes';
import { SurveyEditDisplayTypes } from '../../utils/enums/SurveyEditDisplayTypes';
import { hasFeatureFlag } from '../../utils/hasFeatureFlag';
import { isCustomerVersion } from '../../utils/isCustomerVersion';
import { Content, LeftTray, LeftTrayItem, LeftTrayList, Topbar, TopbarRight } from '../elements';
import { ObjectMetaData } from '../Objects/ObjectMetaData';
import { Spinner } from '../spinners';
import { Component } from '../utils/Component';
import { ConfirmLink } from '../utils/ConfirmLink';
import ObjectTree, { IObjectTreeSelection } from './Common/ObjectTree';
import { ElementGroupAnalysis } from './CRA/ElementGroupAnalysis';
import { ElementGroupVerification } from './CRA/ElementGroupVerification';
import { SurveyEditFMECAAnalysis } from './FMECA/SurveyEditFMECAAnalysis';
import { SurveyMaintenance } from './FMECA/SurveyEditFMECAMaintenance';
import { SurveyVerification } from './FMECA/SurveyEditFMECAVerification';
import { ICategory } from './Interfaces/ICategory';
import { IElement } from './Interfaces/IElement';
import { ISurveyTemplateElementData } from './ISurveyTemplateElementData';
import { ISurveyTemplateElementGroupData } from './ISurveyTemplateElementGroupData';
import { ISurveyTemplateManifestationData } from './ISurveyTemplateManifestationData';
import { ISurveyTemplateUnitData } from './ISurveyTemplateUnitData';
import { EditScopeModal } from './scope/EditScopeModal';
import { SurveyEditVideoStream } from './surveyEdit/SurveyEditVideoStream';
import { SurveyEditUtProjects } from './UTMeasurement/SurveyEditUtProjects';
import { SurveyEditWorkflow } from './workflow/SurveyEditWorkflow';
import {
    SurveyEditDownloadAndDocuments,
    SurveyEditPointcloud,
    SurveyEditSummaryAndAdvice,
    SurveyEditVideos,
    SurveyEditVisualsAndAssessment,
} from '.';

interface ISurveyTemplateProps extends ITranslateProps {
    match: {
        params: {
            id?: string;
            tab?: string;
        };
    };
    location: any;
}

interface ISurveyTemplateState {
    isUploading: boolean;
    isLoading: boolean;
    object?:
        | {
              id: string;
              name: string;
              compositionIsVisible: boolean;
              inspectionStandardId: string;
          }
        | undefined;
    survey?:
        | {
              id: string;
              status: string;
              isCraAvailable: boolean;
              isFmecaAvailable: boolean;
              inspectionStandardType: InspectionStandardTypes;
              canEditSurvey: boolean;
              labels: string[];
              linkToDocuments: string;
              permissions: {
                  survey: {
                      canUpdateWorkflowData: boolean;
                      videoStream: boolean;
                  };
              };
          }
        | undefined;
    isAssetDescriptionVisible?: boolean | undefined;
    categories: ICategory[];
    elements?: IElement[] | undefined;
    elementGroups: { elementGroupName: string; elements: IElement[]; icons?: IObjectTreeIcon[] }[];
    selectedElementId?: string | undefined;
    selectedUnitId?: string | undefined;
    selectedCategoryId?: string | undefined;
    selectedManifestationId?: string | undefined;
    selectedFailureModeId?: string | undefined;
    selectedElementGroupName?: string | null | undefined;
    archiveSelected: boolean;
    objectTree: {
        filterOnElementGroups: boolean;
    };
    scopeExclusions: SurveyScopeExclusion[];
    scopeDefinedScope: string | null;
    isEditScopeModalOpen: boolean;
}

interface ITabElement {
    name: string;
    text: string;
    isActive: boolean;
}

// eslint-disable-next-line no-shadow
enum TabName {
    Videos = 'videos',
    ManageVisuals = 'manageVisuals',
    ConditionAssessment = 'conditionAssessment',
    PointCloud = 'pointcloud',
    Documents = 'documents',
    SummaryAndAdvice = 'summaryAndAdvice',
    Analysis = 'analysis',
    Verification = 'verification',
    Maintenance = 'maintenance',
    General = 'general',
    UtProjects = 'utProjects',
    Workflow = 'workflow',
    VideoStream = 'videoStream',
}

class SurveyTemplate extends Component<ISurveyTemplateProps, ISurveyTemplateState> {
    constructor(props: ISurveyTemplateProps) {
        super(props);

        this.state = {
            categories: [],
            isUploading: false,
            isLoading: true,
            archiveSelected: props.location.query.isFromAnnotations === 'true',
            objectTree: {
                filterOnElementGroups: this.props.location.query.filterOnElementGroups === 'true',
            },
            selectedFailureModeId: this.props.location.query.failureModeId,
            selectedElementGroupName: this.props.location.query.elementGroupName,
            selectedElementId: this.props.location.query.elementId,
            selectedUnitId: this.props.location.query.unitId,
            selectedManifestationId: this.props.location.query.manifestationId,
            elementGroups: [],
            scopeExclusions: [],
            scopeDefinedScope: null,
            isEditScopeModalOpen: false,
        };
    }

    public override async componentDidMount() {
        await this.load();
        await this.loadScope();
    }

    private async loadScope() {
        if (!this.props.match.params.id) {
            return;
        }
        const result = await fetchJson(`/api/surveys/${this.props.match.params.id}/scope`, 'GET');
        if (result.successful && result.json) {
            await this.setState({
                scopeExclusions: result.json.exclusions ?? [],
                scopeDefinedScope: result.json.definedScope ?? null,
            });
        }
    }

    // eslint-disable-next-line complexity
    public override render() {
        if (this.state.isLoading || !this.state.survey || !this.state.object) {
            return (
                <div className="mt40">
                    <Spinner />
                </div>
            );
        }
        if (!this.state.survey.canEditSurvey) {
            return <Navigate to={`/surveys/${this.state.survey.id}`} />;
        }

        // eslint-disable-next-line
        let props: ISurveyTemplateElementData | ISurveyTemplateUnitData | ISurveyTemplateElementGroupData | ISurveyTemplateManifestationData | {};
        if (this.state.selectedManifestationId) {
            const manifestationName = this.getSelectedManifestation()?.name ?? 'undefined';
            props = { manifestationId: this.state.selectedManifestationId, manifestationName };
        } else if (this.state.selectedUnitId) {
            const unitName = this.getSelectedUnit()?.name ?? 'undefined';
            const unitCode = this.getSelectedUnit()?.code ?? 'undefined';
            props = { unitId: this.state.selectedUnitId, unitName, unitCode };
        } else if (this.state.selectedElementId) {
            const elementName = this.getSelectedElement()?.name ?? 'undefined';
            const elementCode = this.getSelectedElement()?.code ?? 'undefined';
            props = { elementId: this.state.selectedElementId, elementName, elementCode };
        } else if (this.state.objectTree.filterOnElementGroups && this.state.selectedElementGroupName) {
            props = { elementGroupName: this.state.selectedElementGroupName };
        } else {
            props = {};
        }

        // eslint-disable-next-line @typescript-eslint/naming-convention
        const CurrentTabPage = this.getCurrentTabPage();

        return (
            <div>
                {this.state.object && (
                    <>
                        <Topbar>
                            <div className="topbar-left">
                                <ul className="nav nav-list nav-list-topbar pull-left">
                                    <li className="pr20">
                                        <h4>
                                            <span className="fa fa-camera pr10"></span>
                                            {this.props.t('survey.editReport')}
                                        </h4>
                                    </li>
                                    {this.getTabs().map((tab) => (
                                        <li key={tab.name} className={classNames({ active: tab.isActive })}>
                                            <ConfirmLink
                                                to={`/surveys/${this.state.survey!.id}/${tab.name}`}
                                                text={tab.text}
                                                showConfirm={this.state.isUploading}
                                                confirmText={this.props.t('survey.cancelUpload')}
                                            />
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <TopbarRight>
                                <Link to={`/surveys/${this.state.survey.id}`} className="btn btn-default btn-sm fw600 ml10">
                                    <span className="fa fa-search pr5"></span> {this.props.t('survey.showReport')}
                                </Link>
                            </TopbarRight>
                        </Topbar>
                        {this.state.isEditScopeModalOpen && this.state.survey && this.state.object && (
                            <EditScopeModal
                                surveyId={this.state.survey.id}
                                objectId={this.state.object.id}
                                initialDefinedScope={this.state.scopeDefinedScope}
                                initialExclusions={this.state.scopeExclusions}
                                onClose={() => void this.setState({ isEditScopeModalOpen: false })}
                                onSaved={() => void this.loadScope()}
                            />
                        )}

                        <Content>
                            <LeftTray>
                                <LeftTrayList>
                                    <LeftTrayItem>{this.state.object.name}</LeftTrayItem>
                                    <LeftTrayItem>
                                        {this.state.survey.linkToDocuments && (
                                            <a href={this.state.survey.linkToDocuments} target="_blank" rel="noreferrer">
                                                {this.props.t('workflow.openLinkToDocuments')}
                                                <i className="fa fa-external-link ml10" />
                                            </a>
                                        )}
                                    </LeftTrayItem>
                                </LeftTrayList>

                                {this.showObjectTree() && this.state.elements && (
                                    <ObjectTree
                                        objectId={this.state.object.id}
                                        surveyId={this.state.survey.id}
                                        categories={this.state.categories}
                                        elements={this.state.elements}
                                        elementGroups={this.state.elementGroups}
                                        isLoading={this.state.isLoading}
                                        isUploading={this.state.isUploading}
                                        exclusions={this.state.scopeExclusions}
                                        onEditScope={() => void this.setState({ isEditScopeModalOpen: true })}
                                        location={this.props.location}
                                        tab={this.getCurrentTabName()}
                                        showUtProjectsIcon={this.getCurrentTabName() === TabName.UtProjects}
                                        onUpdateFilterOnElementGroups={async (value) =>
                                            await this.setState({ objectTree: { ...this.state.objectTree, ...{ filterOnElementGroups: value } } })
                                        }
                                        onUpdateSelection={async (value) => await this.updateObjectTreeSelection(value)}
                                        selection={{
                                            code: '',
                                            elementGroupName: this.state.selectedElementGroupName,
                                            elementId: this.state.selectedElementId,
                                            isArchiveSelected: this.state.archiveSelected,
                                            manifestationId: this.state.selectedManifestationId,
                                            unitId: this.state.selectedUnitId,
                                        }}
                                        options={this.getObjectTreeOptions(this.state.survey.inspectionStandardType)}
                                        filterOnElementGroups={this.state.objectTree.filterOnElementGroups}
                                        inspectionStandardType={this.state.survey.inspectionStandardType}
                                    />
                                )}
                            </LeftTray>
                            {this.getCurrentTabName() === TabName.Videos && (
                                <SurveyEditVideos objectId={this.state.object.id} surveyId={this.state.survey.id} />
                            )}
                            {this.getCurrentTabName() === TabName.PointCloud && (
                                <SurveyEditPointcloud
                                    surveyId={this.state.survey.id}
                                    inspectionStandardType={this.state.survey.inspectionStandardType}
                                />
                            )}
                            {this.getCurrentTabName() === TabName.Documents && (
                                <SurveyEditDownloadAndDocuments
                                    objectId={this.state.object.id}
                                    surveyId={this.state.survey.id}
                                    canAddImages={
                                        this.state.survey.inspectionStandardType === InspectionStandardTypes.Memo ||
                                        this.state.survey.inspectionStandardType === InspectionStandardTypes.Model3d
                                    }
                                />
                            )}

                            {this.getCurrentTabName() === TabName.SummaryAndAdvice && (
                                <SurveyEditSummaryAndAdvice
                                    objectId={this.state.object.id}
                                    surveyId={this.state.survey.id}
                                    status={this.state.survey.status}
                                    isDefaultCompleted={this.state.survey.inspectionStandardType === InspectionStandardTypes.Memo}
                                    isSurveyConditionCalculated={this.state.survey.inspectionStandardType === InspectionStandardTypes.Nen2767}
                                    isConditionAvailable={this.state.survey.inspectionStandardType !== InspectionStandardTypes.Model3d}
                                    isCraAvailable={this.state.survey.isCraAvailable}
                                    isFmecaAvailable={this.state.survey.isFmecaAvailable}
                                    labels={this.state.survey.labels}
                                    surveyInspectionStandardType={this.state.survey.inspectionStandardType}
                                />
                            )}

                            {[TabName.Analysis, TabName.Verification, TabName.Maintenance].includes(this.getCurrentTabName() as TabName) &&
                                this.state.archiveSelected &&
                                this.state.elements && (
                                    <SurveyEditVisualsAndAssessment
                                        objectId={this.state.object.id}
                                        surveyId={this.state.survey.id}
                                        categories={this.state.categories}
                                        elements={this.state.elements}
                                        elementId={this.state.selectedElementId}
                                        unitId={this.state.selectedUnitId}
                                        manifestationId={this.state.selectedManifestationId}
                                        categoryId={this.state.selectedCategoryId}
                                        archiveSelected={this.state.archiveSelected}
                                        displayType={SurveyEditDisplayTypes.Visuals}
                                        inspectionStandardType={this.state.survey.inspectionStandardType}
                                        objectInspectionStandardId={this.state.object.inspectionStandardId}
                                        isAssetDescriptionVisible={this.state.isAssetDescriptionVisible}
                                        onItemChanged={async () => this.loadElements()}
                                        isFmecaAvailable={this.state.survey.isFmecaAvailable}
                                    />
                                )}

                            {this.getCurrentTabName() === TabName.Analysis &&
                                !this.state.archiveSelected &&
                                this.state.survey &&
                                this.state.survey.inspectionStandardType === InspectionStandardTypes.Fmeca &&
                                (this.state.selectedElementGroupName === undefined || this.state.selectedElementId !== undefined) && (
                                    <SurveyEditFMECAAnalysis
                                        elementId={this.state.selectedElementId}
                                        elementGroupName={this.state.selectedElementGroupName}
                                        unitId={
                                            this.state.selectedUnitId ??
                                            (this.state.selectedManifestationId
                                                ? this.findManifestation(this.state.selectedManifestationId)?.unitId
                                                : undefined)
                                        }
                                        manifestationId={this.state.selectedManifestationId}
                                        objectId={this.state.object.id}
                                        surveyId={this.state.survey.id}
                                        failureModeId={this.state.selectedFailureModeId!}
                                        selectedName={this.getObjectTreeSelectionName()}
                                        selectedFailureModeUpdate={async (failureModeId) => this.setState({ selectedFailureModeId: failureModeId })}
                                        isCraAvailable={this.state.survey.isCraAvailable}
                                        isFmecaAvailable={this.state.survey.isFmecaAvailable}
                                        onItemChanged={async () => this.loadElements()}
                                    />
                                )}

                            {this.getCurrentTabName() === TabName.Analysis &&
                                this.state.survey.inspectionStandardType === InspectionStandardTypes.Fmeca &&
                                !this.state.archiveSelected &&
                                this.state.selectedElementGroupName &&
                                this.state.selectedElementId === undefined && (
                                    <ElementGroupAnalysis surveyId={this.state.survey.id} elementGroupName={this.state.selectedElementGroupName} />
                                )}

                            {this.getCurrentTabName() === TabName.Verification &&
                                !this.state.archiveSelected &&
                                this.state.survey &&
                                (this.state.selectedElementGroupName === undefined || this.state.selectedElementId !== undefined) && (
                                    <SurveyVerification
                                        elementId={this.state.selectedElementId}
                                        elementGroupName={this.state.selectedElementGroupName}
                                        unitId={
                                            this.state.selectedUnitId ??
                                            (this.state.selectedManifestationId
                                                ? this.findManifestation(this.state.selectedManifestationId)?.unitId
                                                : undefined)
                                        }
                                        manifestationId={this.state.selectedManifestationId}
                                        objectId={this.state.object.id}
                                        surveyId={this.state.survey.id}
                                        failureModeId={this.state.selectedFailureModeId}
                                        selectedName={this.getObjectTreeSelectionName()}
                                        selectedFailureModeUpdate={async (failureModeId) => this.setState({ selectedFailureModeId: failureModeId })}
                                        isCraAvailable={this.state.survey.isCraAvailable}
                                        isFmecaAvailable={this.state.survey.isFmecaAvailable}
                                        location={this.props.location}
                                        objectTreeFilterOnElementGroups={this.state.objectTree.filterOnElementGroups}
                                        onItemChanged={async () => this.loadElements()}
                                    />
                                )}

                            {this.getCurrentTabName() === TabName.Verification &&
                                !this.state.archiveSelected &&
                                this.state.selectedElementGroupName &&
                                this.state.selectedElementId === undefined && (
                                    <ElementGroupVerification
                                        surveyId={this.state.survey.id}
                                        elementGroupName={this.state.selectedElementGroupName}
                                    />
                                )}

                            {this.getCurrentTabName() === TabName.Maintenance && !this.state.archiveSelected && this.state.survey && (
                                <SurveyMaintenance
                                    elementId={this.state.selectedElementId}
                                    unitId={
                                        this.state.selectedUnitId ??
                                        (this.state.selectedManifestationId
                                            ? this.findManifestation(this.state.selectedManifestationId)?.unitId
                                            : undefined)
                                    }
                                    manifestationId={this.state.selectedManifestationId}
                                    objectId={this.state.object.id}
                                    surveyId={this.state.survey.id}
                                    failureModeId={this.state.selectedFailureModeId}
                                    selectedName={this.getObjectTreeSelectionName()}
                                    selectedFailureModeUpdate={async (failureModeId) => this.setState({ selectedFailureModeId: failureModeId })}
                                    isCraAvailable={this.state.survey.isCraAvailable}
                                    isFmecaAvailable={this.state.survey.isFmecaAvailable}
                                />
                            )}

                            {this.getCurrentTabName() === TabName.ManageVisuals && this.state.elements && (
                                <SurveyEditVisualsAndAssessment
                                    objectId={this.state.object.id}
                                    surveyId={this.state.survey.id}
                                    categories={this.state.categories}
                                    elements={this.state.elements}
                                    elementId={this.state.selectedElementId}
                                    unitId={this.state.selectedUnitId}
                                    manifestationId={this.state.selectedManifestationId}
                                    categoryId={this.state.selectedCategoryId}
                                    archiveSelected={this.state.archiveSelected}
                                    displayType={SurveyEditDisplayTypes.Visuals}
                                    inspectionStandardType={this.state.survey.inspectionStandardType}
                                    objectInspectionStandardId={this.state.object.inspectionStandardId}
                                    isAssetDescriptionVisible={this.state.isAssetDescriptionVisible}
                                    onItemChanged={async () => this.loadElements()}
                                    isFmecaAvailable={this.state.survey.isFmecaAvailable}
                                />
                            )}

                            {this.getCurrentTabName() === TabName.ConditionAssessment && this.state.elements && (
                                <SurveyEditVisualsAndAssessment
                                    objectId={this.state.object.id}
                                    surveyId={this.state.survey.id}
                                    categories={this.state.categories}
                                    elements={this.state.elements}
                                    elementId={this.state.selectedElementId}
                                    unitId={this.state.selectedUnitId}
                                    manifestationId={this.state.selectedManifestationId}
                                    categoryId={this.state.selectedCategoryId}
                                    archiveSelected={this.state.archiveSelected}
                                    displayType={SurveyEditDisplayTypes.Assessment}
                                    inspectionStandardType={this.state.survey.inspectionStandardType}
                                    objectInspectionStandardId={this.state.object.inspectionStandardId}
                                    isAssetDescriptionVisible={this.state.isAssetDescriptionVisible}
                                    onItemChanged={async () => this.loadElements()}
                                    isFmecaAvailable={this.state.survey.isFmecaAvailable}
                                />
                            )}
                            {this.getCurrentTabName() === TabName.General && <ObjectMetaData objectId={this.state.object.id} />}

                            {this.getCurrentTabName() === TabName.UtProjects && this.state.elements && (
                                <SurveyEditUtProjects
                                    objectId={this.state.object.id}
                                    surveyId={this.state.survey.id}
                                    element={this.getSelectedElement()}
                                    unit={this.getSelectedUnit()}
                                    manifestation={this.getSelectedManifestation()}
                                    onItemChanged={async () => this.loadElements()}
                                />
                            )}
                            {this.getCurrentTabName() === TabName.Workflow && <SurveyEditWorkflow surveyId={this.state.survey.id} />}
                            {this.getCurrentTabName() === TabName.VideoStream && <SurveyEditVideoStream surveyId={this.state.survey.id} />}

                            <CurrentTabPage
                                tabName={this.getCurrentTabName()}
                                surveyId={this.state.survey.id}
                                objectId={this.state.object.id}
                                onItemChanged={async () => this.loadElements()}
                                {...props}
                            />
                        </Content>
                    </>
                )}
            </div>
        );
    }

    private async updateObjectTreeSelection(value: IObjectTreeSelection) {
        await this.setState({
            selectedElementId: value.elementId,
            selectedUnitId: value.unitId,
            selectedManifestationId: value.manifestationId,
            selectedFailureModeId: undefined,
            archiveSelected: value.isArchiveSelected,
            selectedElementGroupName: value.elementGroupName,
        });
    }

    private getObjectTreeSelectionName() {
        if (this.state.selectedManifestationId) {
            const manifestation = this.findManifestation(this.state.selectedManifestationId);
            return manifestation?.name;
        } else if (this.state.selectedUnitId) {
            const unit = this.findUnit(this.state.selectedUnitId);
            return unit?.name;
        } else if (this.state.selectedElementId) {
            const element = this.findElement(this.state.selectedElementId);
            return element?.name;
        } else if (this.state.selectedElementGroupName) {
            return this.state.selectedElementGroupName;
        }
        return undefined;
    }

    private getSelectedElement() {
        return this.findElement(this.state.selectedElementId);
    }

    private findElement(elementId?: string) {
        for (const element of this.state.elements ?? []) {
            if (element.id === elementId) {
                return element;
            }
        }
        return undefined;
    }

    private getSelectedUnit() {
        return this.findUnit(this.state.selectedUnitId);
    }

    private findUnit(unitId?: string) {
        for (const element of this.state.elements ?? []) {
            for (const unit of element.units) {
                if (unit.id === unitId) {
                    return unit;
                }
            }
        }
        return undefined;
    }

    private getSelectedManifestation() {
        return this.findManifestation(this.state.selectedManifestationId);
    }

    private findManifestation(manifestationId?: string) {
        for (const element of this.state.elements ?? []) {
            for (const unit of element.units) {
                for (const manifestation of unit.manifestations) {
                    if (manifestation.id === manifestationId) {
                        return manifestation;
                    }
                }
            }
        }
        return undefined;
    }

    private showObjectTree() {
        const settings = this.getInspectionStandardTypeSettings(this.state.survey?.inspectionStandardType as string);
        if (settings) {
            return settings.showObjectTree(this.getCurrentTabName());
        }
        if (
            this.getCurrentTabName() === TabName.ConditionAssessment ||
            this.getCurrentTabName() === TabName.ManageVisuals ||
            this.getCurrentTabName() === TabName.Analysis ||
            this.getCurrentTabName() === TabName.Verification ||
            this.getCurrentTabName() === TabName.Maintenance ||
            this.getCurrentTabName() === TabName.UtProjects
        ) {
            return true;
        }
        return false;
    }

    private getObjectTreeOptions(inspectionStandardType: InspectionStandardTypes) {
        const settings = this.getInspectionStandardTypeSettings(this.state.survey?.inspectionStandardType as string);
        if (settings) {
            return settings.objectTreeOptions;
        }

        return {
            hasElementsGroups: hasFeatureFlag(FeatureFlags.Cra) && inspectionStandardType === InspectionStandardTypes.Fmeca,
            archiveLinkAvailable: this.getCurrentTabName() === TabName.ManageVisuals || inspectionStandardType === InspectionStandardTypes.Fmeca,
        };
    }

    private getCurrentTabName() {
        if (this.props.match.params.tab && this.props.match.params.tab !== 'editReport') {
            return this.props.match.params.tab;
        }
        if (!this.state.survey) {
            return 'loading';
        }
        const settings = this.getInspectionStandardTypeSettings(this.state.survey?.inspectionStandardType as string);
        if (settings) {
            return settings.surveyEditDefaultTabName;
        }
        return TabName.ManageVisuals;
    }

    private getCurrentTabPage(): (
        props: { tabName: string; surveyId: string; objectId: string; onItemChanged: () => Promise<void> } & (
            | ISurveyTemplateElementData
            | ISurveyTemplateUnitData
            | ISurveyTemplateElementGroupData
            | ISurveyTemplateManifestationData
            // eslint-disable-next-line
            | {}
        ),
    ) => JSX.Element | null {
        if (!this.state.survey || !this.state.object) {
            return () => null;
        }
        const settings = this.getInspectionStandardTypeSettings(this.state.survey.inspectionStandardType as string);
        if (settings) {
            // eslint-disable-next-line @typescript-eslint/unbound-method
            return settings.getSurveyEditPage;
        }
        return () => null;
    }

    private getTabs() {
        if (!this.state.survey || !this.state.object) {
            return [];
        }

        const settings = this.getInspectionStandardTypeSettings(this.state.survey.inspectionStandardType as string);
        if (settings) {
            const inspectionStandardTypeTabs = settings.surveyEditTabs;
            return inspectionStandardTypeTabs.map((i) => ({
                name: i.name,
                text: i.text ?? (i.textTranslateKey ? this.props.t(i.textTranslateKey) : `Missing translation for ${i.name}`),
                isActive: this.getCurrentTabName() === i.name,
            }));
        }

        const tabs = [] as ITabElement[];
        const isFmeca = this.state.survey.inspectionStandardType === InspectionStandardTypes.Fmeca;
        const isMemo = this.state.survey.inspectionStandardType === InspectionStandardTypes.Memo;
        const isNen2767 = this.state.survey.inspectionStandardType === InspectionStandardTypes.Nen2767;
        const is3dModel = this.state.survey.inspectionStandardType === InspectionStandardTypes.Model3d;

        if ((isNen2767 || isFmeca) && isCustomerVersion('amsterdam')) {
            tabs.push({ name: TabName.General, text: this.props.t('common.object'), isActive: this.getCurrentTabName() === TabName.General });
        }
        if (isFmeca) {
            tabs.push({
                name: TabName.Analysis,
                text: this.props.t('failureMode.analysis'),
                isActive: this.getCurrentTabName() === TabName.Analysis,
            });
            tabs.push({
                name: TabName.Verification,
                text: this.props.t('failureMode.verification'),
                isActive: this.getCurrentTabName() === TabName.Verification,
            });
            tabs.push({
                name: TabName.Maintenance,
                text: this.props.t('failureMode.maintenance'),
                isActive: this.getCurrentTabName() === TabName.Maintenance,
            });
        }

        if (!isFmeca && !isMemo && !is3dModel) {
            tabs.push({ name: TabName.Videos, text: this.props.t('survey.videosTab'), isActive: this.getCurrentTabName() === TabName.Videos });
        }
        if (this.state.object.compositionIsVisible && !isFmeca && !isMemo && !is3dModel) {
            tabs.push({
                name: TabName.ManageVisuals,
                text: this.props.t('survey.manageVisualsTab'),
                isActive: this.getCurrentTabName() === TabName.ManageVisuals,
            });
            tabs.push({
                name: TabName.ConditionAssessment,
                text: this.props.t('survey.conditionAssessmentTab'),
                isActive: this.getCurrentTabName() === TabName.ConditionAssessment,
            });
        }
        if (isCustomerVersion('ronik')) {
            tabs.push({
                name: TabName.UtProjects,
                text: this.props.t('common.measurements'),
                isActive: this.getCurrentTabName() === TabName.UtProjects,
            });
        }
        if (!isMemo) {
            tabs.push({
                name: TabName.PointCloud,
                text: this.props.t('survey.pointcloudTab'),
                isActive: this.getCurrentTabName() === TabName.PointCloud,
            });
        }
        tabs.push({ name: TabName.Documents, text: this.props.t('survey.documents'), isActive: this.getCurrentTabName() === TabName.Documents });

        if (this.state.survey?.permissions.survey.canUpdateWorkflowData) {
            tabs.push({ name: TabName.Workflow, text: this.props.t('workflow.workflow'), isActive: this.getCurrentTabName() === TabName.Workflow });
        }

        if (this.state.survey?.permissions.survey.videoStream) {
            tabs.push({
                name: TabName.VideoStream,
                text: this.props.t('videoStream.videoStream'),
                isActive: this.getCurrentTabName() === TabName.VideoStream,
            });
        }

        tabs.push({
            name: TabName.SummaryAndAdvice,
            text: this.props.t('survey.summaryAndAdviceTab'),
            isActive: this.getCurrentTabName() === TabName.SummaryAndAdvice,
        });

        return tabs;
    }

    private async load() {
        await this.setState({ isLoading: true });

        const surveyResult = await fetchJson(`/api/surveys/${this.props.match.params.id}`, 'GET');
        if (surveyResult.json) {
            const objectResult = await fetchJson(
                `/api/objects/${surveyResult.json.objectId}?fields=name,compositionIsVisible,inspectionStandardId`,
                'GET',
            );
            await this.setState({
                object: {
                    name: objectResult.json.name,
                    compositionIsVisible: objectResult.json.compositionIsVisible,
                    id: surveyResult.json.objectId,
                    inspectionStandardId: objectResult.json.inspectionStandardId,
                },
                survey: surveyResult.json,
                isAssetDescriptionVisible: surveyResult.json.isAssetDescriptionVisible,
            });
            await this.loadElements();
            await this.setState({ isLoading: false });

            if (customInspectionTypeFrontEndSettings[surveyResult.json.inspectionStandardType]) {
                await this.setState({
                    objectTree: customInspectionTypeFrontEndSettings[surveyResult.json.inspectionStandardType]!.objectTreeDefaults,
                });
            }
        }
    }

    private async loadElements() {
        if (!this.state.object || !this.props.match.params.id || !this.state.survey) {
            return;
        }
        const categoriesResult = await fetchJson(`/api/objects/${this.state.object.id}/categories`, 'GET');
        await this.setState({ categories: categoriesResult.json });

        const settings = customInspectionTypeFrontEndSettings[this.state.survey?.inspectionStandardType as string];
        if (settings?.surveyEditLoadElements) {
            const elementData = await settings.surveyEditLoadElements(this.props.match.params.id);
            if (elementData.elements) {
                await this.setState({ elements: elementData.elements });
            }
            if (elementData.elementGroups) {
                await this.setState({ elementGroups: elementData.elementGroups });
            }
        } else {
            let url = `/api/objects/${this.state.object.id}/elements?surveyId=${this.props.match.params.id}`;
            url += '&includeConditions=true&includeAssetCount=true';
            if (this.state.survey.inspectionStandardType === InspectionStandardTypes.Nen2767) {
                url += '&includeDerivedConditions=true';
            }
            if (isCustomerVersion('ronik')) {
                url += '&includeUtProjects=true';
            }
            const elementsResult = await fetchJson(url, 'GET');
            await this.setState({ elements: elementsResult.json });
        }
    }

    private getInspectionStandardTypeSettings(inspectionStandardType: string) {
        return customInspectionTypeFrontEndSettings[inspectionStandardType];
    }
}

// eslint-disable-next-line import/no-default-export
export default translate()(SurveyTemplate);
